import React, { useRef, useState } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

interface Service {
  title: string;
  problem: string;
  solution: string;
  outcome: string;
  fascinationBullet?: string;
  href: string;
}

interface ServicesSectionProps {
  headline: string;
  subtitle?: string;
  services: Service[];
}

// Service-specific data
const serviceEnhancements: Record<string, {
  icon: React.ReactNode;
  gradient: string;
  accentColor: string;
  stats: { label: string; value: string }[];
  beforeAfter: { before: string; after: string };
}> = {
  'AI Audit Partnership': {
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    gradient: 'from-blue-500 to-cyan-500',
    accentColor: 'blue',
    stats: [
      { label: 'Recent Audit', value: '$180K found' },
      { label: 'Time to Value', value: '1-2 weeks' },
      { label: 'Pays For Itself', value: 'Before build' },
    ],
    beforeAfter: { before: 'Unknown ROI', after: 'Ranked roadmap' },
  },
  'AI Implementation': {
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    gradient: 'from-violet-500 to-purple-500',
    accentColor: 'violet',
    stats: [
      { label: 'Lead Engine', value: '200+/mo' },
      { label: 'Contract Auto', value: '2d → 15m' },
      { label: 'Voice AI', value: '+108% booked' },
    ],
    beforeAfter: { before: '6-12 months', after: '2-8 weeks' },
  },
  'VibeCode Interfaces': {
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
    ),
    gradient: 'from-emerald-500 to-teal-500',
    accentColor: 'emerald',
    stats: [
      { label: 'Dev Speed', value: '5-10x' },
      { label: 'Time Saved', value: '80%' },
      { label: 'Apps Built', value: '45+' },
    ],
    beforeAfter: { before: '3-6 months', after: '2-4 weeks' },
  },
};

// Before/After comparison bar
const BeforeAfterBar: React.FC<{ before: string; after: string; accentColor: string }> = ({
  before,
  after,
  accentColor,
}) => {
  const afterColorClass = {
    blue: 'bg-blue-500/20 text-blue-400',
    violet: 'bg-violet-500/20 text-violet-400',
    emerald: 'bg-emerald-500/20 text-emerald-400',
  }[accentColor] || 'bg-emerald-500/20 text-emerald-400';

  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="px-2 py-1 rounded bg-red-500/10 text-red-400">{before}</span>
      <svg className="w-4 h-4 text-zinc-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
      </svg>
      <span className={`px-2 py-1 rounded ${afterColorClass}`}>{after}</span>
    </div>
  );
};

// Service Card
const ServiceCard: React.FC<{
  service: Service;
  index: number;
}> = ({ service, index }) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [isHovering, setIsHovering] = useState(false);
  const isInView = useInView(cardRef, { once: true, margin: '-50px' });

  const enhancement = serviceEnhancements[service.title] || serviceEnhancements['AI Consulting'];

  const borderColorClass = {
    blue: 'hover:border-blue-500/30',
    violet: 'hover:border-violet-500/30',
    emerald: 'hover:border-emerald-500/30',
  }[enhancement.accentColor] || 'hover:border-blue-500/30';

  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="group relative"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <Link
        to={service.href}
        className={`block h-full relative rounded-2xl bg-zinc-900/50 border border-zinc-800/50 ${borderColorClass} transition-colors duration-300 overflow-hidden`}
      >
        {/* Subtle gradient overlay on hover */}
        <motion.div
          className={`absolute inset-0 bg-gradient-to-br ${enhancement.gradient} opacity-0 group-hover:opacity-[0.03] transition-opacity duration-300`}
        />

        <div className="relative z-10 p-6 h-full flex flex-col">
          {/* Icon */}
          <div className={`w-12 h-12 rounded-xl bg-zinc-800/50 border border-zinc-700/30 flex items-center justify-center text-zinc-400 group-hover:text-white transition-colors mb-5`}>
            {enhancement.icon}
          </div>

          {/* Title */}
          <h3 className="text-xl font-semibold text-white mb-3">
            {service.title}
          </h3>

          {/* Description */}
          <p className="text-zinc-400 text-sm leading-relaxed mb-5 flex-grow">
            {service.solution}
          </p>

          {/* Before/After */}
          <div className="mb-5">
            <BeforeAfterBar
              before={enhancement.beforeAfter.before}
              after={enhancement.beforeAfter.after}
              accentColor={enhancement.accentColor}
            />
          </div>

          {/* Outcome box */}
          <div className="p-4 rounded-xl bg-zinc-800/30 border border-zinc-700/20 mb-5">
            <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Result</p>
            <p className="text-white text-sm font-medium">{service.outcome}</p>
          </div>

          {/* Stats - shown on hover */}
          <AnimatePresence>
            {isHovering && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="mb-5 overflow-hidden"
              >
                <div className="flex justify-between pt-4 border-t border-zinc-800/50">
                  {enhancement.stats.map((stat) => (
                    <div key={stat.label} className="text-center">
                      <p className="text-white font-semibold">{stat.value}</p>
                      <p className="text-[10px] text-zinc-500 uppercase">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Link */}
          <div className="flex items-center gap-2 text-zinc-400 group-hover:text-white transition-colors">
            <span className="text-sm font-medium">Learn more</span>
            <motion.svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              animate={isHovering ? { x: 4 } : { x: 0 }}
              transition={{ duration: 0.2 }}
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </motion.svg>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export const ServicesSection: React.FC<ServicesSectionProps> = ({
  headline,
  subtitle,
  services,
}) => {
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });

  return (
    <section ref={sectionRef} className="relative py-16 sm:py-20 md:py-24 bg-[#0a0a0a] overflow-hidden">
      {/* Subtle background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-zinc-900/20 via-transparent to-zinc-900/20" />

      {/* Top border */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-zinc-800 to-transparent" />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 mb-6 rounded-full bg-zinc-800/50 border border-zinc-700/30"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-zinc-500"></span>
            <span className="text-zinc-400 text-sm">Solutions</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-3xl md:text-4xl font-bold text-white mb-4"
          >
            {headline}
          </motion.h2>

          {subtitle && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-zinc-500 text-lg max-w-2xl mx-auto"
            >
              {subtitle}
            </motion.p>
          )}
        </div>

        {/* Services Grid - Equal 3 columns */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <ServiceCard
              key={index}
              service={service}
              index={index}
            />
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-12 text-center"
        >
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white text-zinc-900 font-semibold hover:bg-zinc-100 transition-colors"
          >
            <span>Start Your AI Journey</span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
          <p className="mt-3 text-zinc-600 text-sm">
            Free consultation included
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;
